'use client';

import { useEffect, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { FileText, X } from 'lucide-react';
import { useAuth } from '@/components/auth-provider';
// version.ts is a plain constant module (no 'server-only', no server imports),
// so the client bundle can import it directly and stay in sync with the
// acceptance stamping on the server.
import { CURRENT_LEGAL_VERSION } from '@/lib/legal/version';
import { shouldShowTosNotice } from '@/lib/legal/notice';

// Human-readable date for the banner copy. Keep in step with
// CURRENT_LEGAL_VERSION (an ISO date) whenever the legal docs are bumped.
const UPDATED_ON = 'July 31, 2026';

const DISMISS_KEY = `rb_tos_notice_${CURRENT_LEGAL_VERSION}`;

/**
 * Slim dismissible banner shown to signed-in users whose recorded terms
 * acceptance predates the currently published legal version. Fetches nothing:
 * it reads the user already loaded by AuthProvider. Dismissal is per legal
 * version via localStorage, and signing in again re-stamps acceptance
 * (lib/legal/acceptance.ts), so the banner naturally retires itself.
 *
 * The session payload exposes `acceptedTermsVersion`; a MISSING value now
 * shows the banner too - unstamped accounts are precisely the people who
 * accepted an older version, and §13's notice promise is to them (the old
 * quiet-on-null reading reached ~2% of accounts).
 */
export function TosUpdateNotice() {
  const t = useT();
  const { user, loading } = useAuth();
  const [dismissed, setDismissed] = useState(true);

  // localStorage is unavailable during SSR; resolve dismissal after mount.
  useEffect(() => {
    try {
      setDismissed(!!window.localStorage.getItem(DISMISS_KEY));
    } catch {
      // Private browsing etc. — show the banner, dismissal just won't persist.
      setDismissed(false);
    }
  }, []);

  if (loading || !user || dismissed) return null;

  const { acceptedTermsVersion, createdAt } = user as {
    acceptedTermsVersion?: string | null;
    createdAt?: string | null;
  };
  if (!shouldShowTosNotice(acceptedTermsVersion, CURRENT_LEGAL_VERSION, createdAt)) {
    return null;
  }

  const dismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // ignore storage errors
    }
    setDismissed(true);
  };

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[190] max-w-[calc(100vw-2rem)]">
      <div className="flex items-center gap-2 rounded-full bg-background/95 border border-border text-foreground text-sm pl-3 pr-1.5 py-1.5 shadow-lg backdrop-blur-sm">
        <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="truncate">Our Terms of Service were updated on {UPDATED_ON}.</span>
        <a
          href="/doc/terms-of-service"
          className="shrink-0 font-medium underline underline-offset-2 hover:no-underline"
        >
          {t('tos.readTerms')}
        </a>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('actions.dismiss')}
          className="shrink-0 rounded-full p-1.5 hover:bg-muted transition-colors"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
